import { useEffect, useMemo, useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { EmailOtpType } from "@supabase/supabase-js";
import { supabase } from "../../lib/supabaseClient";

const NAVY = "#2a2420";
const ORANGE = "#607840";

type Phase = "verifying" | "ready" | "saved" | "error";

function getHashParams() {
  return new URLSearchParams(window.location.hash.replace(/^#/, ""));
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("verifying");
  const [message, setMessage] = useState("Verifying your reset link...");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);

  useEffect(() => {
    let alive = true;

    // supabase-js auto-detects recovery tokens in the URL hash and fires this.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (!alive) return;
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setPhase("ready");
      }
    });

    async function verify() {
      const hashParams = getHashParams();
      const hashError = hashParams.get("error_description") || hashParams.get("error");

      if (hashError) {
        if (!alive) return;
        setPhase("error");
        setMessage(hashError.replace(/\+/g, " "));
        return;
      }

      const tokenHash = queryParams.get("token_hash");
      const type = (queryParams.get("type") || "recovery") as EmailOtpType;

      if (tokenHash) {
        const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
        if (!alive) return;
        if (error) {
          setPhase("error");
          setMessage("This reset link is invalid or has expired. Please request a new one.");
          return;
        }
        setPhase("ready");
        return;
      }

      // Implicit / already-established recovery session.
      const { data } = await supabase.auth.getSession();
      if (!alive) return;
      if (data.session) {
        setPhase("ready");
        return;
      }

      setPhase("error");
      setMessage("This reset link is invalid or has expired. Please request a new one.");
    }

    verify();

    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, [queryParams]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setError(error.message);
        return;
      }
      setPhase("saved");
      window.setTimeout(() => navigate("/gate", { replace: true }), 1400);
    } catch {
      setError("We couldn't update your password. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden relative">

      {/* ── Left: brand panel ── */}
      <div
        className="hidden lg:block"
        style={{
          flex: "0 0 60%",
          backgroundImage: "url(/login-logo.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#2a2420",
        }}
      />

      {/* ── Right: panel ── */}
      <div
        className="flex flex-col justify-center px-10 py-12 w-full lg:w-auto overflow-y-auto"
        style={{ flex: "0 0 40%", backgroundColor: "#fefcf8", borderLeft: "1px solid rgba(80,55,30,0.08)" }}
      >
        <div className="w-full max-w-xs mx-auto">

          {phase === "verifying" && (
            <div className="text-center">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: "rgba(96,120,64,0.1)" }}
              >
                <Loader2 size={28} className="animate-spin" style={{ color: ORANGE }} />
              </div>
              <p className="text-sm leading-relaxed" style={{ color: NAVY, opacity: 0.6 }}>
                {message}
              </p>
            </div>
          )}

          {phase === "error" && (
            <div className="text-center">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: "rgba(220,38,38,0.08)" }}
              >
                <XCircle size={28} style={{ color: "#dc2626" }} />
              </div>
              <h1 className="text-xl font-extrabold mb-2" style={{ color: NAVY }}>
                Link expired
              </h1>
              <p className="text-sm leading-relaxed mb-6" style={{ color: NAVY, opacity: 0.6 }}>
                {message}
              </p>
              <Link
                to="/forgot-password"
                className="inline-block text-sm font-semibold"
                style={{ color: ORANGE }}
              >
                Request a new link
              </Link>
            </div>
          )}

          {phase === "saved" && (
            <div className="text-center">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: "rgba(16,185,129,0.1)" }}
              >
                <CheckCircle size={28} style={{ color: "#059669" }} />
              </div>
              <h1 className="text-xl font-extrabold mb-2" style={{ color: NAVY }}>
                Password updated
              </h1>
              <p className="text-sm leading-relaxed" style={{ color: NAVY, opacity: 0.6 }}>
                Taking you to your account…
              </p>
            </div>
          )}

          {phase === "ready" && (
            <>
              <h1 className="text-2xl font-extrabold mb-1 text-center" style={{ color: NAVY }}>
                Set a new password
              </h1>
              <p className="text-sm mb-8 text-center" style={{ color: NAVY, opacity: 0.5 }}>
                Choose a new password for your account.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-1.5 text-center" style={{ color: NAVY }}>
                    New password
                  </label>
                  <input
                    type="password"
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all"
                    style={{ border: `1.5px solid ${NAVY}22`, color: NAVY, backgroundColor: "#f8f3ea" }}
                    onFocus={(e) => (e.currentTarget.style.border = `1.5px solid ${ORANGE}`)}
                    onBlur={(e) => (e.currentTarget.style.border = `1.5px solid ${NAVY}22`)}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1.5 text-center" style={{ color: NAVY }}>
                    Confirm password
                  </label>
                  <input
                    type="password"
                    required
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all"
                    style={{ border: `1.5px solid ${NAVY}22`, color: NAVY, backgroundColor: "#f8f3ea" }}
                    onFocus={(e) => (e.currentTarget.style.border = `1.5px solid ${ORANGE}`)}
                    onBlur={(e) => (e.currentTarget.style.border = `1.5px solid ${NAVY}22`)}
                  />
                </div>

                {error && (
                  <p className="text-sm text-center" style={{ color: "#991b1b" }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-2.5 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: ORANGE, boxShadow: "0 3px 12px rgba(96,120,64,0.3)" }}
                >
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  {saving ? "Saving…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
